import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography, Card, CardContent, Grid, Button, TextField, Dialog, DialogActions,
  DialogContent, DialogTitle, CircularProgress, Alert, Box, Chip, Avatar, Divider,
  InputAdornment, IconButton, Paper, Stack, Tab, Tabs
} from '@mui/material';
import {
  Check, Close, Person, Business, Email, Phone, Info, Search, Refresh,
  Visibility, AssignmentInd, HelpOutline,
  History, Description, Room, PhotoLibrary, ZoomIn
} from '@mui/icons-material';
import { getImageUrl } from '../utils/imageUrl';

const DocImage = ({ title, src, onZoom }) => {
  if (!src) return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', backgroundColor: '#f9fafb', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Description sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', mt: 0.5 }}>Not Uploaded</Typography>
    </Paper>
  );

  const fullUrl = src.startsWith('data:') ? src : getImageUrl(src);

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', height: '100%', position: 'relative', '&:hover .zoom-overlay': { opacity: 1 } }}>
      <Box sx={{ p: 1, backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" fontWeight="bold" noWrap>{title}</Typography>
        <IconButton size="small" onClick={() => onZoom(fullUrl, title)} sx={{ p: 0.5 }}>
          <ZoomIn fontSize="small" color="primary" />
        </IconButton>
      </Box>
      <Box
        onClick={() => onZoom(fullUrl, title)}
        sx={{
          height: 120,
          backgroundImage: `url(${fullUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          cursor: 'pointer',
          position: 'relative'
        }}
      >
        <Box className="zoom-overlay" sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)', color: 'white', display: 'flex',
          alignItems: 'center', justifyContent: 'center', opacity: 0, transition: '0.2s opacity'
        }}>
          <ZoomIn />
        </Box>
      </Box>
    </Paper>
  );
};

const OwnerApproval = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState('all');

  // Detail Modal & Action Modal State
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve', 'reject', 'request_info'
  const [inputReason, setInputReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Lightbox Zoom State
  const [zoomImage, setZoomImage] = useState(null);

  useEffect(() => {
    fetchOwners();
  }, []);

  const fetchOwners = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/admin/pending-owners');
      setOwners(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch owner verification requests.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAction = (owner, type) => {
    setSelectedOwner(owner);
    setActionType(type);
    setInputReason('');
    setActionDialogOpen(true);
  };

  const handleExecuteAction = async () => {
    if (!selectedOwner) return;
    setActionLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      if (actionType === 'approve') {
        await axios.put(`/admin/approve-owner/${selectedOwner._id}`, { note: inputReason });
        setSuccessMsg(`Owner ${selectedOwner.name} has been approved.`);
      } else if (actionType === 'reject') {
        if (!inputReason.trim()) {
          setError('Rejection reason is required.');
          setActionLoading(false);
          return;
        }
        await axios.put(`/admin/reject-owner/${selectedOwner._id}`, { reason: inputReason });
        setSuccessMsg(`Owner ${selectedOwner.name} application rejected.`);
      } else if (actionType === 'request_info') {
        if (!inputReason.trim()) {
          setError('Information request details are required.');
          setActionLoading(false);
          return;
        }
        await axios.put(`/admin/request-info-owner/${selectedOwner._id}`, { details: inputReason });
        setSuccessMsg(`Information request sent to ${selectedOwner.name}.`);
      }

      setActionDialogOpen(false);
      setDetailModalOpen(false);
      fetchOwners();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to execute owner status update.');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (owner) => {
    const status = owner.verificationStatus || (owner.isVerified ? 'approved' : 'pending');
    switch (status) {
      case 'approved':
        return <Chip label="Approved" color="success" size="small" icon={<Check />} />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" icon={<Close />} />;
      case 'info_requested':
        return <Chip label="Info Requested" color="warning" size="small" icon={<HelpOutline />} />;
      default:
        return <Chip label="Pending Review" color="info" size="small" icon={<Info />} />;
    }
  };

  const filteredOwners = owners.filter((owner) => {
    const status = owner.verificationStatus || (owner.isVerified ? 'approved' : 'pending');
    const matchesTab = filterTab === 'all' ? true : status === filterTab;
    const matchesSearch =
      owner.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      owner.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      owner.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      owner.phoneNumber?.includes(searchTerm);
    return matchesTab && matchesSearch;
  });

  return (
    <Box sx={{ p: 1 }}>
      {/* Header Banner */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#1e293b' }}>
            Owner Approval Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review owner credentials, business documentation, identity proofs, and process verification actions.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchOwners} disabled={loading}>
          Refresh List
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Filter Tabs & Search Bar */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Tabs
              value={filterTab}
              onChange={(e, val) => setFilterTab(val)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
            >
              <Tab label={`All (${owners.length})`} value="all" />
              <Tab label={`Pending (${owners.filter(o => !o.verificationStatus || o.verificationStatus === 'pending').length})`} value="pending" />
              <Tab label={`Info Requested (${owners.filter(o => o.verificationStatus === 'info_requested').length})`} value="info_requested" />
              <Tab label={`Rejected (${owners.filter(o => o.verificationStatus === 'rejected').length})`} value="rejected" />
            </Tabs>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search owner by name, business, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Owners Cards Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredOwners.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No Owner Requests Found</Typography>
          <Typography variant="body2" color="text.disabled">
            {searchTerm ? 'No owners matching your search filter.' : 'All owner verification requests have been processed.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredOwners.map((owner) => (
            <Grid item xs={12} md={6} lg={4} key={owner._id}>
              <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  {/* Status & Name */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: '#0284c7', width: 44, height: 44 }}>
                        {owner.name?.charAt(0) || 'O'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: 180 }}>
                          {owner.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Business fontSize="inherit" /> {owner.businessName || 'N/A'}
                        </Typography>
                      </Box>
                    </Stack>
                    {getStatusChip(owner)}
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Owner Key Details */}
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#334155' }}>
                      <Phone fontSize="small" color="action" /> {owner.phoneNumber}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#334155' }} noWrap>
                      <Email fontSize="small" color="action" /> {owner.email}
                    </Typography>
                    {owner.gst && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#334155' }}>
                        <AssignmentInd fontSize="small" color="action" /> GST: <strong>{owner.gst}</strong>
                      </Typography>
                    )}
                    {owner.address && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#334155' }} noWrap>
                        <Room fontSize="small" color="action" /> {owner.address}
                      </Typography>
                    )}
                  </Stack>

                  {/* Document Thumbnail Previews */}
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Uploaded Documents
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <DocImage title="Business License / GST" src={owner.businessLicense || owner.gstDoc} onZoom={(url, title) => setZoomImage({ url, title })} />
                    </Grid>
                    <Grid item xs={6}>
                      <DocImage title="Identity Proof" src={owner.idProof || owner.aadharDoc} onZoom={(url, title) => setZoomImage({ url, title })} />
                    </Grid>
                  </Grid>

                  {/* Requested Note / Reason Warning if applicable */}
                  {owner.requestedInfoNote && (
                    <Alert severity="warning" sx={{ py: 0.5, px: 1, mb: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                      <strong>Info Requested:</strong> {owner.requestedInfoNote}
                    </Alert>
                  )}
                  {owner.rejectionReason && (
                    <Alert severity="error" sx={{ py: 0.5, px: 1, mb: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                      <strong>Rejection Reason:</strong> {owner.rejectionReason}
                    </Alert>
                  )}
                </CardContent>

                <Divider />

                {/* Card Action Buttons */}
                <Box sx={{ p: 1.5, backgroundColor: '#f8fafc', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => { setSelectedOwner(owner); setDetailModalOpen(true); }}
                    sx={{ flexGrow: 1 }}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<Check />}
                    onClick={() => handleOpenAction(owner, 'approve')}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<HelpOutline />}
                    onClick={() => handleOpenAction(owner, 'request_info')}
                  >
                    Request Info
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Close />}
                    onClick={() => handleOpenAction(owner, 'reject')}
                  >
                    Reject
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Comprehensive Owner Detail Modal */}
      {selectedOwner && (
        <Dialog open={detailModalOpen} onClose={() => setDetailModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: '#0284c7' }}>{selectedOwner.name?.charAt(0)}</Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">{selectedOwner.name}</Typography>
                <Typography variant="caption" color="text.secondary">{selectedOwner.businessName}</Typography>
              </Box>
            </Box>
            {getStatusChip(selectedOwner)}
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3 }}>
            {/* Applicant Full Details Section */}
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person fontSize="small" /> Applicant Profile & Business Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#fafafa' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Full Name</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedOwner.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Business / Enterprise Name</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedOwner.businessName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedOwner.phoneNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Email Address</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedOwner.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">GST Number</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedOwner.gst || 'Not Provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">PAN Number</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedOwner.pan || 'Not Provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Aadhar Number</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedOwner.aadhar || 'Not Provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedOwner.address || 'Not Provided'}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Documents Section */}
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhotoLibrary fontSize="small" /> Uploaded Supporting Documents
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <DocImage title="Business License / GST" src={selectedOwner.businessLicense || selectedOwner.gstDoc} onZoom={(url, title) => setZoomImage({ url, title })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DocImage title="Identity Proof (Aadhar/PAN)" src={selectedOwner.idProof || selectedOwner.aadharDoc} onZoom={(url, title) => setZoomImage({ url, title })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DocImage title="Address Proof" src={selectedOwner.addressProof} onZoom={(url, title) => setZoomImage({ url, title })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DocImage title="Owner Photograph" src={selectedOwner.ownerPhoto} onZoom={(url, title) => setZoomImage({ url, title })} />
              </Grid>
            </Grid>

            {/* Approval History Log */}
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History fontSize="small" /> Verification Audit & History Log
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc' }}>
              {selectedOwner.approvalHistory && selectedOwner.approvalHistory.length > 0 ? (
                <Stack spacing={1.5}>
                  {selectedOwner.approvalHistory.map((item, idx) => (
                    <Box key={idx} sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, borderLeft: '4px solid #0284c7' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                          Action: {item.action?.replace('_', ' ')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                      {item.reason && <Typography variant="body2" color="text.secondary">Note/Reason: {item.reason}</Typography>}
                      {item.requestedDetails && <Typography variant="body2" color="warning.main">Requested Details: {item.requestedDetails}</Typography>}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No prior approval history recorded. This is an initial verification request.
                </Typography>
              )}
            </Paper>
          </DialogContent>

          <DialogActions sx={{ p: 2, gap: 1, backgroundColor: '#f1f5f9' }}>
            <Button onClick={() => setDetailModalOpen(false)} color="inherit">Close</Button>
            <Button variant="outlined" color="warning" startIcon={<HelpOutline />} onClick={() => handleOpenAction(selectedOwner, 'request_info')}>
              Request Information
            </Button>
            <Button variant="outlined" color="error" startIcon={<Close />} onClick={() => handleOpenAction(selectedOwner, 'reject')}>
              Reject Owner
            </Button>
            <Button variant="contained" color="success" startIcon={<Check />} onClick={() => handleOpenAction(selectedOwner, 'approve')}>
              Approve Owner
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Action Processing Dialog (Approve / Reject / Request Info) */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' && 'Approve Owner Registration'}
          {actionType === 'reject' && 'Reject Owner Application'}
          {actionType === 'request_info' && 'Request Additional Information'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applicant: <strong>{selectedOwner?.name}</strong> ({selectedOwner?.businessName})
          </Typography>

          {actionType === 'approve' && (
            <TextField
              fullWidth
              label="Approval Note (Optional)"
              placeholder="e.g. Documents verified against business registry"
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
            />
          )}

          {actionType === 'reject' && (
            <TextField
              fullWidth
              required
              label="Rejection Reason"
              placeholder="e.g. Invalid business license or address proof mismatch"
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
              error={!inputReason.trim()}
              helperText="Please provide a clear reason for rejection."
            />
          )}

          {actionType === 'request_info' && (
            <TextField
              fullWidth
              required
              label="Requested Information & Instructions"
              placeholder="e.g. Please re-upload a clearer copy of your GST registration certificate and ID proof."
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
              error={!inputReason.trim()}
              helperText="Specify what information or documents the owner must provide."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setActionDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === 'approve' ? 'success' : actionType === 'reject' ? 'error' : 'warning'}
            onClick={handleExecuteAction}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={18} /> : actionType === 'approve' ? <Check /> : actionType === 'reject' ? <Close /> : <HelpOutline />}
          >
            Confirm {actionType.replace('_', ' ')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Lightbox Preview Modal */}
      {zoomImage && (
        <Dialog open={Boolean(zoomImage)} onClose={() => setZoomImage(null)} maxWidth="lg">
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {zoomImage.title}
            <IconButton onClick={() => setZoomImage(null)}><Close /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', p: 1 }}>
            <img src={zoomImage.url} alt={zoomImage.title} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default OwnerApproval;
